import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import searchIcon from "./img/search.png";
import filterIcon from "./img/filter.png";
import plusIcon from "./img/plus.png";
import CreateCommunity from "./CreateCommunity";
import { getAccessToken } from "../../awsAuth";
import "./Communities.scss";

import notificationsOnIcon from "./img/notificationsOn.png";
import notificationsOffIcon from "./img/notificationsOff.png";

import { UI_TTL, HARD_TTL, cacheGetWithAge, cacheSet } from "../../utils/swrcache";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../contexts/SubscriptionContext";

import DynamicHeader from "../Header/DynamicHeader";
import CommunitiesFilter from "./CommunitiesFilter";

// import Tips from "../Tips/Tips";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const isCanceledRequest = (e) => {
  return (
    e?.name === "AbortError" ||
    e?.name === "CanceledError" ||
    e?.code === "ERR_CANCELED" ||
    axios.isCancel?.(e)
  );
};

const TABS = [
  { key: "all", label: "Усі" },
  { key: "subs", label: "Підписки" },
  { key: "my", label: "Мої" },
  { key: "community", label: "Спільноти" },
  { key: "channel", label: "Канали" },
];

const SORTS = [
  { key: "members", label: "За кількістю учасників" },
  { key: "created", label: "За датою створення" },
];

const FREE_COMMUNITY_LIMIT = 3;
const UNREAD_COMMUNITY_POLL_MS = 20000;

const Communities = () => {
  const { userId } = useAuth();
  const { isActive: _isProActive } = useSubscription();
  const isProActive = false;

  /* state */
  const [communities, setCommunities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("communities:activeTab");
    return saved || "all";
  });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [privacy, setPrivacy] = useState(
    () => localStorage.getItem("communities:privacy") || "any"
  );
  const [sortKey, setSortKey] = useState(
    () => localStorage.getItem("communities:sortKey") || "created"
  );

  const [listFading, setListFading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [enterWaveKey, setEnterWaveKey] = useState(0);

  // const [showCommunitiesTip, setShowCommunitiesTip] = useState(false); // ✅ DISABLED: прибрали Tips підказку

  const navigate = useNavigate();
  const CACHE_KEY = "communities:list";

  // ✅ DISABLED: логіка автопоказу Tips у Communities
  // useEffect(() => {
  //   const seen = localStorage.getItem("communities:introSeen");
  //
  //   if (!seen) {
  //     const t = setTimeout(() => {
  //       setShowCommunitiesTip(true);
  //       localStorage.setItem("communities:introSeen", "1");
  //     }, 450);
  //
  //     return () => clearTimeout(t);
  //   }
  // }, []);

  useEffect(() => {
    const handler = (e) => {
      const { communityId, totalMembers } = e?.detail || {};
      if (!communityId || typeof totalMembers !== "number") return;

      setCommunities((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const next = list.map((c) =>
          String(c.id) === String(communityId)
            ? { ...c, membersCount: totalMembers }
            : c
        );

        try {
          cacheSet(CACHE_KEY, next);
        } catch {}

        return next;
      });
    };

    window.addEventListener("cv:communityMembersChanged", handler);
    return () =>
      window.removeEventListener("cv:communityMembersChanged", handler);
  }, []);

  /* fetch with cache */
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setIsLoading(true);

    const cached = cacheGetWithAge(CACHE_KEY, HARD_TTL);
    if (cached && !cancelled) {
      const list = Array.isArray(cached.data) ? cached.data : [];
      setCommunities(list);
      setTimeout(() => !cancelled && setIsLoading(false), 180);
    }

    const fetchFresh = async () => {
      let timeoutId;
      try {
        timeoutId = setTimeout(() => controller.abort(), 6000);

        const res = await axios.get("/api/communities", {
          signal: controller.signal,
        });

        const data = res.data;
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setCommunities(list);
          cacheSet(CACHE_KEY, list);
          setIsLoading(false);
          setEnterWaveKey((k) => k + 1);
        }
      } catch (err) {
        if (!isCanceledRequest(err)) {
          console.error("Load communities failed", err);
        }
        if (!cancelled) setIsLoading(false);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    if (!cached) {
      fetchFresh();
    } else if (cached.ageMs <= UI_TTL) {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(fetchFresh, { timeout: 2000 });
      } else {
        setTimeout(fetchFresh, 0);
      }
    } else {
      fetchFresh();
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  /* list enter/fade */
  useEffect(() => {
    setListFading(true);
    const t = setTimeout(() => {
      setListFading(false);
      setEnterWaveKey((k) => k + 1);
    }, 160);
    return () => clearTimeout(t);
  }, [searchTerm, activeTab, privacy, sortKey]);

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleCommunityCreated = () => {
    axios
      .get("/api/communities")
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : [];
        setCommunities(list);
        cacheSet(CACHE_KEY, list);
        setEnterWaveKey((k) => k + 1);
      })
      .catch((err) => console.error("Refresh communities failed", err));
  };

  const onTabChange = useCallback((key) => {
    setActiveTab(key);
    localStorage.setItem("communities:activeTab", key);
  }, []);

  const onClearFilters = useCallback(() => {
    setPrivacy("any");
    setSortKey("created");
    localStorage.removeItem("communities:privacy");
    localStorage.removeItem("communities:sortKey");
  }, []);

  /* create modal guard */
  const handleOpenCreate = async () => {
  if (!userId) {
    navigate("/auth", { state: { step: "login" } });
    return;
  }

  try {
    const token = await getAccessToken();

    const res = await axios.get("/api/communities/owned/limits", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = res.data;

    if (!res.status || res.status < 200 || res.status >= 300) {
      throw new Error(data?.error || "Failed");
    }

    if (!data.canCreate) {
      alert("Наразі ви можете мати не більше 3 активних спільнот.");
      return;
    }

    setShowCreateModal(true);
  } catch {
    setShowCreateModal(true);
  }
};

  /* filter/sort */
    /* subscriptions + unread polling */
  const [subsById, setSubsById] = useState({});
  const [unreadById, setUnreadById] = useState({});
  const pollAbortRef = useRef(null);

  /* filter/sort */
  const filtered = useMemo(() => {
    const normMode = (m) => (m === "channel" ? "channel" : "community");

    let list = Array.isArray(communities) ? communities.slice() : [];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter((c) => (c.name || "").toLowerCase().includes(q));
    }

    if (activeTab === "community" || activeTab === "channel") {
      list = list.filter((c) => normMode(c.mode) === activeTab);
    } else if (activeTab === "my") {
      list = userId ? list.filter((c) => c.creatorId === userId) : [];
    } else if (activeTab === "subs") {
      list = userId
        ? list.filter((c) => !!(subsById?.[c.id]?.enabled ?? false))
        : [];
    }

    if (privacy === "open") list = list.filter((c) => c.isPrivate !== true);
    else if (privacy === "closed") list = list.filter((c) => c.isPrivate === true);

    if (sortKey === "members") {
      list.sort((a, b) => (b.membersCount || 0) - (a.membersCount || 0));
    } else if (sortKey === "created") {
      list.sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
      );
    }

    return list;
  }, [communities, searchTerm, activeTab, privacy, sortKey, userId, subsById]);

  /* owner lock set (visual) */
  const myLockedIdSet = useMemo(() => {
    const set = new Set();
    return set;
  }, [communities, userId, isProActive]);

  useEffect(() => {
    if (!userId) {
      setSubsById({});
      setUnreadById({});
      return;
    }

    let timerId;
    let cancelled = false;

    const loadSubs = async () => {
      try {
        const token = await getAccessToken();
        const r = await axios.get("/api/communities/subscriptions", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = r.data;
        const map = {};
        (data?.items || []).forEach((it) => {
          map[it.communityId] = {
            enabled: !!it.enabled,
            lastReadAt: Number(it.lastReadAt || 0) || 0,
          };
        });
        if (!cancelled) setSubsById(map);
      } catch (e) {
        if (!isCanceledRequest(e)) {
          console.debug("[Communities] subscriptions fetch error:", e?.message || e);
        }
      }
    };

    const loadUnreadBulk = async () => {
      if (!userId) return;
      try {
        pollAbortRef.current?.abort?.();
        pollAbortRef.current = new AbortController();

        const token = await getAccessToken();
        const r = await axios.get("/api/communities/unread-bulk", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: pollAbortRef.current.signal,
        });

        const data = r.data;
        const map = {};
        (data?.items || []).forEach(
          (it) => (map[it.communityId] = Number(it.unread || 0))
        );
        if (!cancelled) setUnreadById(map);
      } catch (e) {
        if (!isCanceledRequest(e)) {
          console.debug("[Communities] unread fetch error:", e?.message || e);
        }
      }
    };

    const onExternalToggle = (ev) => {
      const { communityId, enabled } = ev?.detail || {};
      if (!communityId || typeof enabled !== "boolean") return;
      setSubsById((prev) => {
        const cur = prev[communityId] || { enabled: true, lastReadAt: 0 };
        return { ...prev, [communityId]: { ...cur, enabled } };
      });
    };

    const onFocus = () => {
      loadSubs();
      loadUnreadBulk();
    };

    const init = async () => {
      await loadSubs();
      await loadUnreadBulk();

      if (cancelled) return;

      timerId = setInterval(loadUnreadBulk, UNREAD_COMMUNITY_POLL_MS);
      window.addEventListener("focus", onFocus);
      window.addEventListener("cv:communityNotifToggled", onExternalToggle);
    };

    init();

    return () => {
      cancelled = true;
      clearInterval(timerId);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("cv:communityNotifToggled", onExternalToggle);
      pollAbortRef.current?.abort?.();
    };
  }, [userId]);

  /* block scroll under create modal */
  useEffect(() => {
    if (!showCreateModal) return;
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = "hidden";
    return () => {
      body.style.overflow = prev;
    };
  }, [showCreateModal]);

  /* notifications toggle */
  const toggleNotifications = useCallback(
    async (communityId) => {
      if (!userId) return;

      const curEnabled = !!(subsById[communityId]?.enabled ?? true);
      const next = !curEnabled;

      setSubsById((prev) => {
        const cur = prev[communityId] || { enabled: true, lastReadAt: 0 };
        return { ...prev, [communityId]: { ...cur, enabled: next } };
      });

      try {
        const res = await axios.post(
          `/api/communities/${communityId}/notifications`,
          {
            userId,
            enabled: next,
          }
        );
        if (!res.status || res.status < 200 || res.status >= 300) {
          setSubsById((prev) => {
            const cur = prev[communityId] || { enabled: true, lastReadAt: 0 };
            return { ...prev, [communityId]: { ...cur, enabled: curEnabled } };
          });
          const txt =
            (res?.data && (res.data.error || res.data.message)) || "";
          alert(`Не вдалося оновити сповіщення. ${txt || ""}`.trim());
          return;
        }
      } catch (e) {
        setSubsById((prev) => {
          const cur = prev[communityId] || { enabled: true, lastReadAt: 0 };
          return { ...prev, [communityId]: { ...cur, enabled: curEnabled } };
        });
        alert("Не вдалося оновити сповіщення. Перевірте мережу.");
        return;
      }

      try {
        window.dispatchEvent(
          new CustomEvent("cv:communityNotifToggled", {
            detail: { communityId, enabled: next },
          })
        );
      } catch {}
    },
    [subsById, userId]
  );

  const markCommunityRead = useCallback(
    async (communityId) => {
      setUnreadById((prev) => ({ ...prev, [communityId]: 0 }));
      setSubsById((prev) => {
        const cur = prev[communityId] || { enabled: true, lastReadAt: 0 };
        return {
          ...prev,
          [communityId]: { ...cur, lastReadAt: Date.now() },
        };
      });
      try {
      const token = await getAccessToken();
      await axios.post(
        `/api/communities/${communityId}/mark-read`,
        {},
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);
      } catch {}
    },
    [userId]
  );

  const isFilterActive = privacy !== "any" || sortKey !== "created";
  const activeTabIndex = useMemo(
    () => Math.max(0, TABS.findIndex((t) => t.key === activeTab)),
    [activeTab]
  );

  return (
    <>
      <DynamicHeader
        title="Спільноти"
        variant="glass"
        size="compact"
        hideOnScroll={true}
        showBack={true}
        showMenu={false}
      />

      {/* Tips overlay у Communities */}
      {/* {showCommunitiesTip && (
        <div className="tipsIslandWrap">
          <Tips
            variant="communitiesIntro"
            className="tipsCard--island"
            remember={false}
            onClose={() => setShowCommunitiesTip(false)}
            onAction={() => {
              setShowCommunitiesTip(false);
              handleOpenCreate();
            }}
          />
        </div>
      )} */}

      <div className={`communities communities--has-local-header`}>
        <div className="communities__header">
          <div className="communities__search">
            <img src={searchIcon} alt="search" />
            <input
              type="text"
              placeholder="Пошук спільнот..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button
            className={`communities__filter ${filtersOpen ? "is-open" : ""}`}
            title="Фільтри"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen ? "true" : "false"}
          >
            <img src={filterIcon} alt="filter" />
            {isFilterActive && (
              <span className="filter-indicator" aria-hidden="true">
                ✓
              </span>
            )}
          </button>
        </div>

        <div
          className="communities__tabs tabs--slider"
          role="tablist"
          aria-label="Тип спільнот"
          style={{ "--tabs": TABS.length, "--tab-index": activeTabIndex }}
        >
          <span className="tabs__slider" aria-hidden />
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={activeTab === t.key}
              className={`communities__tab ${
                activeTab === t.key ? "is-active" : ""
              }`}
              onClick={() => onTabChange(t.key)}
            >
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        <div
          className={`communities__list ${
            listFading ? "is-fading" : ""
          } ${!isLoading ? "is-entering" : "is-loading"}`}
          data-wave={enterWaveKey}
        >
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div className="community-card community-card--skeleton" key={`s${i}`}>
                <div className="skel skel__avatar" />
                <div className="skel__block">
                  <div className="skel skel__line skel__line--lg" />
                  <div className="skel skel__line skel__line--sm" />
                </div>
                <div className="skel skel__bell" />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <p className="no-communities">
              Нічого не знайдено. Спробуй змінити фільтри або запит.
            </p>
          ) : (
            filtered.map((community, idx) => {
              const isChannel = community?.mode === "channel";
              const isOwner = userId && community?.creatorId === userId;
              const locked = isOwner ? myLockedIdSet.has(community.id) : false;

              const sub = subsById[community.id] || {
                enabled: true,
                lastReadAt: 0,
              };
              const unread = Number(unreadById[community.id] || 0);
              const bellEnabled = !!sub.enabled;

              const onCardClick = () => {
                if (locked) {
                  alert("Наразі ви можете мати не більше 3 активних спільнот.");
                  return;
                }
                if (userId && subsById?.[community.id]) {
                  markCommunityRead(community.id);
                }

                const url = userId
                  ? `/communities/${community.id}?userId=${encodeURIComponent(
                      userId
                    )}`
                  : `/communities/${community.id}`;
                navigate(url);
              };

              const onBellClick = (e) => {
                e.stopPropagation();
                if (!userId) return;
                toggleNotifications(community.id);
              };

              return (
                <div
                  className="community-card"
                  key={community.id}
                  onClick={onCardClick}
                  style={{
                    opacity: locked ? 1 : 1,
                    cursor: locked ? "pointer" : "pointer",
                    "--i": idx,
                  }}
                >
                  <img
                    className="community-card__avatar"
                    src={community.avatar || DEFAULT_AVATAR}
                    alt={community.name || "Community"}
                    loading="lazy"
                    decoding="async"
                    width={48}
                    height={48}
                    sizes="48px"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_AVATAR;
                    }}
                  />
                  <div className="community-card__info">
                    <div className="community-card__row">
                      <h3 title={community.name}>{community.name}</h3>
                      <span
                        className={`badge ${
                          isChannel ? "badge--channel" : "badge--community"
                        }`}
                        title={
                          isChannel
                            ? "Канал: постить лише власник"
                            : "Спільнота: постити можуть усі"
                        }
                      >
                        {isChannel ? "Канал" : "Спільнота"}
                      </span>
                      {isOwner && locked && (
                        <span className="community-card__lock-hint">
                          🔒 Без Premium
                        </span>
                      )}
                    </div>

                    <div className="community-card__meta">
                      <span>{community.membersCount || 0} учасників</span>
                      <span className="dot">•</span>
                      <span>{community.isPrivate ? "Закрита" : "Відкрита"}</span>
                      {isOwner && (
                        <>
                          <span className="dot">•</span>
                          <span>Моя</span>
                        </>
                      )}
                    </div>
                  </div>

                  {userId && (
                    <button
                      className="community-card__bell"
                      onClick={onBellClick}
                      aria-label={
                        bellEnabled
                          ? "Вимкнути сповіщення"
                          : "Увімкнути сповіщення"
                      }
                      title={
                        bellEnabled
                          ? "Вимкнути сповіщення"
                          : "Увімкнути сповіщення"
                      }
                    >
                      <img
                        src={bellEnabled ? notificationsOnIcon : notificationsOffIcon}
                        alt=""
                      />
                      {unread > 0 && (
                        <span
                          className={`cv-badge ${
                            unread > 99 ? "cv-badge--max" : ""
                          } ${bellEnabled ? "" : "cv-badge--muted"}`}
                        >
                          {unread > 99 ? "99+" : unread}
                        </span>
                      )}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        <button
          className="communities__create-btn"
          onClick={handleOpenCreate}
          title="Створити спільноту"
        >
          <img src={plusIcon} alt="create" />
        </button>

        {showCreateModal && (
          <CreateCommunity
            onClose={() => setShowCreateModal(false)}
            onCommunityCreated={handleCommunityCreated}
          />
        )}

        <CommunitiesFilter
          open={filtersOpen}
          privacy={privacy}
          sortKey={sortKey}
          onChangePrivacy={(v) => {
            if (v === "any") localStorage.removeItem("communities:privacy");
            else localStorage.setItem("communities:privacy", v);
            setPrivacy(v);
          }}
          onChangeSortKey={(v) => {
            if (v === "created") localStorage.removeItem("communities:sortKey");
            else localStorage.setItem("communities:sortKey", v);
            setSortKey(v);
          }}
          onClear={onClearFilters}
          onClose={() => setFiltersOpen(false)}
          sorts={SORTS}
        />
      </div>
    </>
  );
};

export default Communities;